import { useState, useEffect, useRef } from "react";
import { valueTracker } from "../../../mocks/dashboard";

function AnimatedNumber({ target, prefix = "", suffix = "", decimals = 0 }: {
  target: number; prefix?: string; suffix?: string; decimals?: number;
}) {
  const [current, setCurrent] = useState(0);
  const ref = useRef(false);

  useEffect(() => {
    if (ref.current) return;
    ref.current = true;
    const duration = 1200;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setCurrent(target * ease);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [target]);

  const display = decimals > 0
    ? current.toFixed(decimals)
    : Math.floor(current).toLocaleString();

  return <span>{prefix}{display}{suffix}</span>;
}

export default function ValueTrackerSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const maxValue = Math.max(...valueTracker.history.map((h) => h.value));
  const maxHours = Math.max(...valueTracker.history.map((h) => h.hours));

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const metrics = [
    {
      icon: "ri-time-line",
      color: "#00D873",
      bgColor: "#E8F5E9",
      label: "절약한 업무 시간",
      total: valueTracker.savedHours,
      totalSuffix: "h",
      totalDecimals: 1,
      thisMonth: `+${valueTracker.savedHoursThisMonth}h`,
      thisMonthLabel: "이번 달",
      desc: "AI 자동화로 되찾은 시간",
    },
    {
      icon: "ri-money-dollar-circle-line",
      color: "#FF8C00",
      bgColor: "#FFF3E0",
      label: "만들어낸 예상 가치",
      total: valueTracker.estimatedValue,
      totalPrefix: "₩",
      totalSuffix: "",
      totalDecimals: 0,
      thisMonth: `+₩${(valueTracker.estimatedValueThisMonth / 10000).toFixed(0)}만`,
      thisMonthLabel: "이번 달",
      desc: "절약 시간 × 시급 환산",
    },
    {
      icon: "ri-trophy-line",
      color: "#E91E63",
      bgColor: "#FCE4EC",
      label: "완성된 수익 프로젝트",
      total: valueTracker.completedProjects,
      totalSuffix: "개",
      totalDecimals: 0,
      thisMonth: `+${valueTracker.completedProjectsThisMonth}개`,
      thisMonthLabel: "이번 달",
      desc: "이모티콘·쇼츠·외주 등",
    },
  ];

  return (
    <div
      ref={ref}
      className={`bg-white rounded-2xl p-6 transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#00D873]/10 text-[#00D873]">
              AI 가치 트래커
            </span>
          </div>
          <h3 className="text-base font-extrabold text-[#191F28]">내가 만들어낸 가치</h3>
          <p className="text-xs text-[#8B95A1] mt-0.5">토스처럼 확인하는 AI 성과 지표</p>
        </div>
        <div className="text-2xl">💸</div>
      </div>

      {/* 3 Metric Cards */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {metrics.map((m, i) => (
          <div
            key={m.label}
            className="rounded-xl p-4 flex flex-col gap-1.5 transition-all duration-500"
            style={{
              backgroundColor: m.bgColor,
              transitionDelay: `${i * 100}ms`,
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(12px)",
            }}
          >
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`${m.icon} text-sm`} style={{ color: m.color }} />
              </span>
              <span className="text-[10px] font-semibold text-[#191F28]/60 leading-tight">{m.label}</span>
            </div>
            <div className="text-xl font-black text-[#191F28]">
              {visible && (
                <AnimatedNumber
                  target={m.total}
                  prefix={m.totalPrefix}
                  suffix={m.totalSuffix}
                  decimals={m.totalDecimals}
                />
              )}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: m.color }}>
                {m.thisMonth}
              </span>
              <span className="text-[10px] text-[#8B95A1]">{m.thisMonthLabel}</span>
            </div>
            <p className="text-[10px] text-[#8B95A1] leading-tight">{m.desc}</p>
          </div>
        ))}
      </div>

      {/* Weekly Bar Chart */}
      <div className="border-t border-[#F2F4F6] pt-4">
        <p className="text-xs font-semibold text-[#8B95A1] mb-3">주간 절약 시간 추이</p>
        <div className="flex items-end gap-2 h-16">
          {valueTracker.history.map((h, i) => (
            <div key={h.week} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full rounded-t-md bg-[#F2F4F6] relative overflow-hidden" style={{ height: "48px" }}>
                <div
                  className="absolute bottom-0 left-0 right-0 rounded-t-md bg-[#00D873] transition-all duration-700"
                  style={{
                    height: visible ? `${(h.hours / maxHours) * 100}%` : "0%",
                    transitionDelay: `${i * 120 + 300}ms`,
                  }}
                />
              </div>
              <span className="text-[9px] text-[#8B95A1] whitespace-nowrap">{h.week}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="mt-4 flex items-center justify-between bg-[#F2F4F6] rounded-xl px-4 py-3">
        <div>
          <p className="text-xs font-bold text-[#191F28]">더 많은 챌린지 완료 시</p>
          <p className="text-xs text-[#8B95A1]">절약 시간이 늘어납니다</p>
        </div>
        <span className="text-xs font-bold text-[#00D873] cursor-pointer whitespace-nowrap">챌린지 하러 가기 →</span>
      </div>
    </div>
  );
}
